import React from 'react';
import Icon from '../../../components/AppIcon';
import {
  GridDotsDash,
  CrosshairMarkDash,
  AngularAccentDash,
  HandDrawnUnderlineDash,
  DiagonalStripesDash,
  useScrollReveal,
} from './DashboardGeometricPatterns';

const RoadConditionsCard = () => {
  const [cardRef, cardVisible] = useScrollReveal({ threshold: 0.1 });

  const conditions = [
    {
      id: 1,
      region: 'Oslo',
      status: 'good',
      temperature: '12°C',
      visibility: 'God',
      icon: 'Sun'
    },
    {
      id: 2,
      region: 'Bergen',
      status: 'moderate',
      temperature: '8°C',
      visibility: 'Moderat',
      icon: 'CloudRain'
    },
    {
      id: 3,
      region: 'Trondheim',
      status: 'poor',
      temperature: '2°C',
      visibility: 'Dårlig',
      icon: 'CloudSnow'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'good': return 'var(--color-success)';
      case 'moderate': return 'var(--color-warning)';
      case 'poor': return 'var(--color-error)';
      default: return 'var(--color-muted-foreground)';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'good': return 'Gode forhold';
      case 'moderate': return 'Moderate forhold';
      case 'poor': return 'Vanskelige forhold';
      default: return 'Ukjent';
    }
  };

  return (
    <div
      ref={cardRef}
      className="bg-card border-2 border-border rounded-[var(--radius-md)] p-4 sm:p-5 lg:p-6"
      style={{
        position: 'relative',
        overflow: 'hidden',
        opacity: cardVisible ? 1 : 0,
        transform: cardVisible ? 'translateY(0px)' : 'translateY(22px)',
        transition: 'opacity 0.65s cubic-bezier(0.22,1,0.36,1) 0.12s, transform 0.65s cubic-bezier(0.22,1,0.36,1) 0.12s',
      }}
    >
      {/* SVG geometric overlays */}
      <GridDotsDash rows={4} cols={6} style={{ position: 'absolute', top: '-4px', right: '-4px', opacity: 0.8 }} />
      <DiagonalStripesDash
        style={{ position: 'absolute', bottom: '-10px', right: '-10px', width: '100px', height: '100px', opacity: 0.5 }}
        color="#FF5722"
        opacity={0.07}
      />
      <CrosshairMarkDash style={{ position: 'absolute', top: '14px', right: '14px' }} size={14} color="#004F98" />
      <AngularAccentDash style={{ position: 'absolute', bottom: '12px', left: '12px' }} width={44} height={36} color="#004F98" />

      <div className="flex items-center justify-between mb-4 sm:mb-5 lg:mb-6">
        <div>
          <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-foreground font-heading">
            Veiforhold
          </h2>
          <HandDrawnUnderlineDash width={88} className="mt-1" />
        </div>
        <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground font-caption">
          <Icon name="Clock" size={16} />
          <span>Oppdatert 09:20</span>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {conditions?.map((condition, idx) => (
          <div
            key={condition?.id}
            className="p-4 bg-muted border-2 border-transparent hover:border-primary rounded-[var(--radius-sm)] transition-all"
            style={{
              position: 'relative',
              overflow: 'hidden',
              opacity: cardVisible ? 1 : 0,
              transform: cardVisible ? 'translateY(0px)' : 'translateY(16px)',
              transition: `opacity 0.55s cubic-bezier(0.22,1,0.36,1) ${0.2 + idx * 0.1}s, transform 0.55s cubic-bezier(0.22,1,0.36,1) ${0.2 + idx * 0.1}s, box-shadow 200ms ease-out`,
              cursor: 'default',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 10px 28px rgba(0,0,0,0.2)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0px)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-base sm:text-lg font-medium text-foreground mb-1">
                  {condition?.region}
                </div>
                <div
                  className="text-xs sm:text-sm font-medium"
                  style={{ color: getStatusColor(condition?.status) }}
                >
                  {getStatusLabel(condition?.status)}
                </div>
              </div>
              <div
                className="w-10 h-10 sm:w-12 sm:h-12 rounded flex items-center justify-center"
                style={{ background: `${getStatusColor(condition?.status)}15`, transition: 'transform 300ms cubic-bezier(0.22,1,0.36,1)' }}
              >
                <Icon
                  name={condition?.icon}
                  size={20}
                  color={getStatusColor(condition?.status)}
                  style={{ transition: 'transform 300ms cubic-bezier(0.22,1,0.36,1)' }}
                />
              </div>
            </div>

            <div className="flex items-center gap-4 text-xs sm:text-sm text-muted-foreground font-caption">
              <div className="flex items-center gap-1">
                <Icon name="Thermometer" size={14} />
                <span>{condition?.temperature}</span>
              </div>
              <div className="flex items-center gap-1">
                <Icon name="Eye" size={14} />
                <span>{condition?.visibility}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RoadConditionsCard;